/**
 * Sentry Configuration for Frontend Error Tracking
 * 
 * Captures unhandled exceptions with context:
 * - role, tenantId, appointmentId
 * - release version + environment (pilot/prod)
 * - route/context
 */

// Only initialize in production or if SENTRY_DSN is set
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.MODE || 'development';
const RELEASE = import.meta.env.VITE_APP_VERSION || '0.0.0';

// Lazy load Sentry to avoid bundle size impact if not configured
let Sentry = null;

export async function initSentry() {
  if (!SENTRY_DSN) {
    console.log('[Sentry] Not configured (VITE_SENTRY_DSN not set)');
    return;
  }

  try {
    // Dynamic import to reduce bundle size
    // This will fail gracefully if @sentry/react is not installed
    const SentryModule = await import('@sentry/react').catch(() => {
      console.warn('[Sentry] @sentry/react package not installed. Install it with: npm install @sentry/react');
      return null;
    });
    
    if (!SentryModule) {
      console.log('[Sentry] Skipping initialization (package not installed)');
      return;
    }
    
    Sentry = SentryModule;

    Sentry.init({
      dsn: SENTRY_DSN,
      environment: ENVIRONMENT,
      release: RELEASE,
      
      // Performance monitoring
      tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,
      
      // Session replay (optional, can be expensive)
      replaysSessionSampleRate: 0.0, // Disabled for now
      replaysOnErrorSampleRate: 0.0, // Disabled for now
      
      // Filter out noisy errors
      ignoreErrors: [
        // Browser extensions
        'ResizeObserver loop limit exceeded',
        'Non-Error promise rejection captured',
        // Network errors (handled separately)
        'NetworkError',
        'Failed to fetch',
      ],
      
      // Capture unhandled promise rejections
      captureUnhandledRejections: true,
      
      // Before send hook to add context
      beforeSend(event) {
        // Add user context if available
        const userContext = getCurrentUserContext();
        if (userContext) {
          event.user = {
            id: userContext.userId,
            email: userContext.email,
            role: userContext.role,
            tenant_id: userContext.tenantId,
          };
          
          event.tags = {
            ...event.tags,
            tenant_id: userContext.tenantId,
            role: userContext.role,
          };
        }
        
        // Add route context
        if (window.location) {
          event.tags = {
            ...event.tags,
            route: window.location.pathname,
          };
        }
        
        return event;
      },
    });

    console.log('[Sentry] Initialized successfully');
  } catch (error) {
    console.error('[Sentry] Failed to initialize:', error);
  }
}

/**
 * Get current user context from AuthContext
 * This is called dynamically to avoid circular dependencies
 */
function getCurrentUserContext() {
  try {
    // Try to get from localStorage or global state
    // This is a fallback - ideally we'd pass context from AuthContext
    const authData = localStorage.getItem('supabase.auth.token');
    if (authData) {
      const parsed = JSON.parse(authData);
      return {
        userId: parsed?.user?.id,
        email: parsed?.user?.email,
        // Role and tenant would need to come from AuthContext
        // For now, we'll set these via setUserContext
      };
    }
  } catch (error) {
    // Ignore
  }
  return null;
}

/**
 * Set user context for Sentry
 * Call this when user logs in or context changes
 */
export function setSentryUserContext(user) {
  if (!Sentry) return;
  
  try {
    Sentry.setUser({
      id: user?.id,
      email: user?.email,
      role: user?.tenant_role || user?.platform_role,
      tenant_id: user?.tenant_id,
    });
    
    Sentry.setTag('tenant_id', user?.tenant_id);
    Sentry.setTag('role', user?.tenant_role || user?.platform_role);
  } catch (error) {
    console.error('[Sentry] Failed to set user context:', error);
  }
}

/**
 * Clear user context (on logout)
 */
export function clearSentryUserContext() {
  if (!Sentry) return;
  
  try {
    Sentry.setUser(null);
  } catch (error) {
    console.error('[Sentry] Failed to clear user context:', error);
  }
}

/**
 * Capture exception manually
 */
export function captureException(error, context = {}) {
  if (!Sentry) {
    console.error('[Sentry] Not initialized, error:', error);
    return;
  }
  
  try {
    Sentry.captureException(error, {
      contexts: {
        custom: context,
      },
    });
  } catch (err) {
    console.error('[Sentry] Failed to capture exception:', err);
  }
}

/**
 * Capture message manually
 */
export function captureMessage(message, level = 'info', context = {}) {
  if (!Sentry) {
    console.log(`[Sentry] Not initialized, message: ${message}`);
    return;
  }
  
  try {
    Sentry.captureMessage(message, {
      level,
      contexts: {
        custom: context,
      },
    });
  } catch (err) {
    console.error('[Sentry] Failed to capture message:', err);
  }
}

export default {
  initSentry,
  setSentryUserContext,
  clearSentryUserContext,
  captureException,
  captureMessage,
};
